import { createFileRoute } from '@tanstack/react-router'
import {
  Layers,
  SlidersHorizontal,
  KeyRound,
  FileVideo,
  Scissors,
  Search,
} from 'lucide-react'
import { APP_NAME, PRICE_MONTHLY_USD } from '@/config/kwykedit'
import { Button } from '@/components/ui/button'

export const Route = createFileRoute('/routes/')({
  component: LandingPage,
})

const FEATURES = [
  {
    id: 'timeline',
    icon: Layers,
    title: 'Multi-track timeline',
    description:
      'Arrange video, audio, text, and image clips across tracks. Trim, split, ripple-delete, and snap with a zoomable timeline.',
  },
  {
    id: 'effects',
    icon: SlidersHorizontal,
    title: 'Effects and color',
    description:
      'Adjust brightness, contrast, saturation, and blur, or build looks with filters and LUT-style grading — previewed instantly.',
  },
  {
    id: 'keyframes',
    icon: KeyRound,
    title: 'Keyframes',
    description:
      'Animate position, scale, rotation, and opacity over time with precise keyframe control on every clip.',
  },
  {
    id: 'export',
    icon: FileVideo,
    title: 'Export MP4 and WebM',
    description:
      'Render finished cuts to MP4 or WebM in resolutions up to 4K, right from the browser. No queue, no watermark.',
  },
  {
    id: 'scene-cuts',
    icon: Scissors,
    title: 'Histogram scene cuts',
    description:
      'Detect hard cuts automatically with local histogram analysis, then jump between scenes or split them onto the timeline.',
  },
  {
    id: 'search',
    icon: Search,
    title: 'Semantic search',
    description:
      'Find shots by meaning — “sunset over water”, “whiteboard”, “crowd” — with on-device embeddings over your own media.',
  },
] as const

function LandingPage() {
  const year = new Date().getFullYear()
  return (
    <div className="relative isolate min-h-screen font-[family-name:'Open_Sans',sans-serif] text-neutral-100 select-text">
      {/* Fixed backdrop: save the artwork as public/brand/landing-bg.jpg */}
      <div
        aria-hidden
        className="fixed inset-0 -z-10 bg-black bg-cover bg-center"
        style={{
          backgroundImage: 'url(/brand/landing-bg.jpg)',
          backgroundAttachment: 'fixed',
        }}
      />
      <div aria-hidden className="fixed inset-0 -z-10 bg-black/70" />
      {/* Top bar */}
      <header className="border-b border-neutral-800">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4">
          <a href="/" className="flex items-center gap-3" aria-label="KWYKEDIT home">
            <img src="/brand/logo.png" alt="KWYKEDIT" className="h-8 w-auto" />
          </a>
          <nav className="flex items-center gap-2">
            <Button asChild variant="ghost" size="sm" className="text-neutral-300 hover:text-white">
              <a href="/affiliates">Affiliate Program</a>
            </Button>
            <Button asChild variant="ghost" size="sm" className="text-neutral-300 hover:text-white">
              <a href="/login">Log in</a>
            </Button>
            <Button asChild size="sm" className="bg-white text-black hover:bg-neutral-200">
              <a href="/signup">Sign up</a>
            </Button>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 py-16 sm:py-24">
        <div className="mx-auto flex max-w-3xl flex-col items-center text-center">
          <img src="/brand/logo.png" alt={`${APP_NAME} logo`} className="mb-6 h-16 w-auto" />
          <h1 className="mb-4 text-4xl font-bold tracking-tight text-white sm:text-5xl">
            Cut Promos fast in your Browser.
          </h1>
          <p className="mb-8 max-w-xl text-lg text-neutral-400">
            KWYKEDIT is an eFed focused editor: timeline cutting, effects, keyframes, and
            exports that run on your machine. Your footage stays with you — nothing is
            uploaded.
          </p>
          <div className="flex flex-col items-center gap-3 sm:flex-row">
            <Button asChild size="lg" className="bg-white px-8 text-black hover:bg-neutral-200">
              <a href="/signup">Sign up</a>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="border-neutral-700 px-8 text-neutral-200 hover:bg-neutral-900 hover:text-white"
            >
              <a href="/login">Log in</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-neutral-800 px-6 py-16">
        <div className="mx-auto max-w-5xl">
          <h2 className="mb-2 text-center text-3xl font-bold tracking-tight text-white">
            Everything for a clean cut
          </h2>
          <p className="mx-auto mb-10 max-w-2xl text-center text-neutral-400">
            Six focused tools cover the full pass from rough assembly to final render —
            all processed locally on your device.
          </p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feature) => (
              <div
                key={feature.id}
                className="rounded-xl border border-neutral-800 bg-neutral-950 p-5"
              >
                <div className="mb-3 flex h-9 w-9 items-center justify-center rounded-md bg-neutral-800">
                  <feature.icon className="h-4 w-4 text-white" />
                </div>
                <h3 className="mb-1 font-semibold text-white">{feature.title}</h3>
                <p className="text-sm leading-relaxed text-neutral-400">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="border-t border-neutral-800 px-6 py-16">
        <div className="mx-auto max-w-md text-center">
          <h2 className="mb-8 text-3xl font-bold tracking-tight text-white">One plan. Full editor. Cancel anytime.</h2>
          <div className="rounded-xl border border-neutral-700 bg-neutral-950 p-8">
            <p className="text-sm font-medium text-neutral-400">Monthly</p>
            <p className="mt-2 text-5xl font-bold text-white">${PRICE_MONTHLY_USD}</p>
            <p className="mt-1 text-sm text-neutral-500">per month</p>
            <ul className="mt-6 space-y-2 text-left text-sm text-neutral-300">
              <li>Full timeline, effects, and keyframes</li>
              <li>MP4 and WebM export</li>
              <li>Scene-cut detection and semantic search</li>
              <li>Discount codes accepted</li>
              <li>Cancel anytime</li>
            </ul>
            <Button asChild size="lg" className="mt-8 w-full bg-white text-black hover:bg-neutral-200">
              <a href="/pricing">See pricing</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-neutral-800 px-6 py-8">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-3 text-sm text-neutral-500 sm:flex-row">
          <span>
            © {year} {APP_NAME}
          </span>
          <div className="flex items-center gap-5">
            <a href="/affiliates" className="text-neutral-300 underline-offset-4 hover:text-white hover:underline">
              Affiliate Program
            </a>
            <a href="/docs" className="text-neutral-300 underline-offset-4 hover:text-white hover:underline">
              Docs
            </a>
          </div>
        </div>
      </footer>
    </div>
  )
}
